import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";
import "./Auth.css";
import logo from '../assets/logo.png';
import { GoogleLogin } from '@react-oauth/google';
import { Clock, Users, ShieldCheck, ArrowLeft } from 'lucide-react';
import AppStoreBadge from '../assets/a_store.png';
import GooglePlayBadge from '../assets/g_play.png';

// Názov firmy sa pri registrácii nepýta — doplní ho onboarding wizard.
// Používateľské meno sa tiež nepýta: ukladáme username = e-mail, presne ako
// Google signin (auth.controller: „Use email as username for uniqueness").
const schema = yup.object({
    email: yup.string().email("E-mail nemá správny formát").required("Zadajte e-mail"),
    password: yup.string().min(6, "Heslo musí mať aspoň 6 znakov").required("Zadajte heslo"),
}).required();

const Register = () => {
    const [successful, setSuccessful] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const handleRegister = (data) => {
        setMessage("");
        setSuccessful(false);
        setLoading(true);

        // username = e-mail (bez samostatného poľa, konzistentné s Google signinom)
        AuthService.register(data.email, data.email, data.password).then(
            (response) => {
                setLoading(false);
                setMessage(response.data.message);
                setSuccessful(true);
            },
            (error) => {
                setLoading(false);
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();

                setMessage(resMessage);
                setSuccessful(false);
            }
        );
    };

    const handleGoogleLoginSuccess = (credentialResponse) => {
        setLoading(true);
        AuthService.googleLogin(credentialResponse.credential).then(
            () => {
                window.location.href = "/dashboard";
            },
            (error) => {
                setLoading(false);
                const resMessage =
                    (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                    error.message ||
                    error.toString();
                setMessage(resMessage);
                setSuccessful(false);
            }
        );
    };

    const handleGoogleLoginError = () => {
        console.log('Login Failed');
        setMessage("Registrácia cez Google zlyhala. Skúste to znova.");
    };

    return (
        <div className="auth-container">
            <div className="auth-promo-panel">
                <div className="promo-content">
                    <div className="promo-header">
                        <img src={logo} alt="Effzone Logo" className="promo-logo" />
                        <div className="promo-logo-text">
                            <span className="promo-logo-title">Effzone</span>
                            <span className="promo-logo-subtitle">Dochádzka a komunikácia</span>
                        </div>
                    </div>
                    <h1 className="promo-title">Dochádzka a&nbsp;firemná komunikácia v&nbsp;jednej&nbsp;appke.</h1>
                    <ul className="promo-features">
                        <li><Clock size={20} /><span>QR dochádzka bez papierovačiek</span></li>
                        <li><Users size={20} /><span>Zamestnanci v systéme na pár klikov</span></li>
                        <li><ShieldCheck size={20} /><span>Bezpečné a v súlade s GDPR</span></li>
                    </ul>
                    <div className="promo-stores">
                        <a href="https://apps.apple.com/sk/app/effzone/id6747626666" aria-label="Download on the App Store">
                            <img src={AppStoreBadge} alt="Download on the App Store" className="promo-store-badge" />
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=scan.sbs.app" aria-label="Get it on Google Play">
                            <img src={GooglePlayBadge} alt="Get it on Google Play" className="promo-store-badge" />
                        </a>
                    </div>
                </div>
            </div>
            <div className="auth-form-panel">
                <div className="login-card">
                    <div className="auth-card-topbar">
                        <a href="https://effzone.sk" className="back-to-website-link">
                            <ArrowLeft size={16} />
                            <span>Späť na web</span>
                        </a>
                        <span className="auth-topbar-login">
                            Máte účet? <Link to="/login">Prihlásiť sa</Link>
                        </span>
                    </div>
                    <div className="auth-form-header-mobile">
                        <img src={logo} alt="Effzone Logo" className="promo-logo" />
                        <div className="promo-logo-text">
                            <span className="promo-logo-title">Effzone</span>
                            <span className="promo-logo-subtitle">Dochádzka a komunikácia</span>
                        </div>
                    </div>
                    <h2 className="title">Vytvorte si firemný účet</h2>
                    <p className="subtitle">Prvé 3 mesiace zadarmo — bez platobnej karty.</p>

                    {!successful ? (
                        <>
                            <div className="google-login-container">
                                <GoogleLogin
                                    onSuccess={handleGoogleLoginSuccess}
                                    onError={handleGoogleLoginError}
                                    theme="outline"
                                    text="continue_with"
                                    shape="rectangular"
                                    width={340}
                                    align="center"
                                    locale="sk"
                                />
                            </div>

                            <div className="or-divider">
                                <span className="line"></span>
                                <span className="or-text">ALEBO CEZ E-MAIL</span>
                                <span className="line"></span>
                            </div>

                            <form onSubmit={handleSubmit(handleRegister)}>
                                <div className="form-group">
                                    <label>Firemný e-mail</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        {...register("email")}
                                        placeholder="jan@vasafirma.sk"
                                    />
                                    {errors.email && <p className="error-message">{errors.email?.message}</p>}
                                </div>
                                <div className="form-group">
                                    <label>Heslo</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        {...register("password")}
                                        placeholder="Aspoň 6 znakov"
                                    />
                                    {errors.password && <p className="error-message">{errors.password?.message}</p>}
                                </div>

                                <div className="form-group">
                                    <button className="btn btn-primary btn-block" disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>Vytvoriť účet</span>
                                    </button>
                                </div>
                                <p className="input-description input-description--center">Na e-mail pošleme overovací odkaz.</p>
                            </form>

                            <div className="auth-footer-link">
                                Už máte účet? <Link to="/login">Prihláste sa</Link>
                            </div>
                        </>
                    ) : (
                        <div className="alert alert-success" role="alert">
                            {message}
                            <br />
                            <Link to="/login" className="alert-link">Prejsť na prihlásenie</Link>
                        </div>
                    )}

                    {message && !successful && (
                        <div className="form-group mt-3">
                            <div className="alert alert-danger" role="alert">
                                {message}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;




